import Modal from "@components/atoms/Modal";
import useScreenOptionsHooks from "../../../../../hooks/useScreenOptionsHooks";
import styled from "styled-components/native";
import { Button, Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import OrderSummary from "./OrderSummary";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";

const ReviewChanges = ({
  onClose,
  handleCreateContract,
  selectedPlan,
  additionalUsers,
  updatedPlan,
  updatedAdditionalUsers,
  lines,
  totals,
  isLoading,
}: {
  onClose: () => void;
  handleCreateContract: (type: "confirm_plan" | "modify_plan") => void;
  selectedPlan: any;
  additionalUsers: any;
  updatedPlan: any;
  updatedAdditionalUsers: any;
  lines: any;
  totals: any;
  isLoading: boolean;
}) => {
  const { t, typography, theme } = useScreenOptionsHooks();
  const ChangeRow = ({
    label,
    oldValue,
    newValue,
  }: {
    label: string;
    oldValue: string;
    newValue: string;
  }) => (
    <TextContainer>
      <KeyContainer>
        <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
          {label}
        </VDBText>
      </KeyContainer>

      <Spacer size={SpaceType.Double} />

      <VDBText
        type={typography["Body-R"]}
        color={theme["core-05"]}
        {...(newValue && { textDecoration: "line-through" })}
      >
        {oldValue}
      </VDBText>

      {newValue && (
        <>
          <Spacer size={SpaceType.Basic} />

          <VDBIcon
            name={VDBIcons.RIGHT}
            size={16}
            color={theme["core-06"]}
            variant={IconsVariant.VDBCustom}
          />

          <Spacer size={SpaceType.Basic} />

          <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
            {newValue}
          </VDBText>
        </>
      )}
    </TextContainer>
  );

  return (
    <>
      <PlanDetails>
        <ChangeRow
          label={t("reviewChanges.popUp.planType.text", "Plan")}
          oldValue={selectedPlan?.label}
          newValue={
            updatedPlan?.plan_type === selectedPlan?.plan_type
              ? ""
              : updatedPlan?.label
          }
        />
        <Spacer size={SpaceType.Basic} />
        <ChangeRow
          label={t("reviewChanges.popUp.users.text", "Users")}
          oldValue={additionalUsers + " " + `users`}
          newValue={
            updatedAdditionalUsers === additionalUsers
              ? ""
              : updatedAdditionalUsers + " " + `users`
          }
        />
        <Spacer size={SpaceType.Double} />
        <OrderSummary lines={lines} estimatedTotal={totals.estimatedTotal} />
        <Spacer size={SpaceType.Double} />
        <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
          {t(
            "reviewChanges.popUp.disclaimer.text",
            "Updating your plan will generate a new invoice and replace the previous one."
          )}
        </VDBText>
        <Spacer size={SpaceType.large} />
        <ButtonContainer>
          <Button
            title={t("reviewChanges.popUp.button.cancel.text", "Cancel")}
            onPress={onClose}
            type={ButtonType.secondary}
            category={ButtonCategory.default}
            width={"48%"}
            disabled={isLoading}
          />
          <Button
            title={t("reviewChanges.popUp.button.confirm.text", "Confirm")}
            onPress={() => handleCreateContract("modify_plan")}
            type={ButtonType.primary}
            category={ButtonCategory.default}
            width={"48%"}
            loading={isLoading}
            disabled={isLoading}
          />
        </ButtonContainer>
      </PlanDetails>
    </>
  );
};

const ReviewChangesPopup = ({
  isOpen,
  onClose,
  handleCreateContract,
  selectedPlan,
  additionalUsers,
  updatedPlan,
  updatedAdditionalUsers,
  lines,
  totals,
  isLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  handleCreateContract: (type: "confirm_plan" | "modify_plan") => void;
  selectedPlan: any;
  additionalUsers: any;
  updatedPlan: any;
  updatedAdditionalUsers: any;
  lines: any;
  totals: any;
  isLoading: boolean;
}) => {
  const { t } = useScreenOptionsHooks();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      paddingLeft={16}
      paddingRight={16}
      paddingBottom={40}
      paddingTop={16}
      headerTitle={t(
        "reviewChanges.popUp.title.text",
        "Review and Confirm Your Changes"
      )}
    >
      <ReviewChanges
        onClose={onClose}
        handleCreateContract={handleCreateContract}
        selectedPlan={selectedPlan}
        additionalUsers={additionalUsers}
        updatedPlan={updatedPlan}
        updatedAdditionalUsers={updatedAdditionalUsers}
        lines={lines}
        totals={totals}
        isLoading={isLoading}
      />
    </Modal>
  );
};

const PlanDetails = styled.View``;

const KeyContainer = styled.View`
  width: 10%;
`;

const TextContainer = styled.View`
  flex-direction: row;
`;

const ButtonContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  gap: 16px;
`;

export default ReviewChangesPopup;
